import { green, grey } from "@ant-design/colors"
import { DatePicker, Progress, Rate, Row, Space, Tooltip } from "antd"
import { useContext, useEffect } from "react"
import { sum } from "../../helpers/array"
import { Section } from "./sectionsList"
import ChecklistContext from "./ChecklistContext"
import { isItemChecked } from "./checklistModel"
import SectionIcon from "./SectionIcon"
import dayjs from 'dayjs'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFlagCheckered } from "@fortawesome/pro-solid-svg-icons"
import { AuthenticationContext } from "../../AuthenticationContext"

type Props = {
  section: Section
  primaryGoal: string | null
  primaryGoalDate: string | null
  primaryGoalCompletedDate: string | null
  onPrimaryGoalChange: (section: string) => void
  onPrimaryGoalDateChange: (date: string | null) => void
  onPrimaryGoalCompletedDateChange: (date: string | null) => void
}

const remainingStyle = {color: grey[3]}

const buildRemaining = (stepsRemaining: number, approximateMinutesRemaining: number, minutesPer?: string) => {
  if (stepsRemaining > 0) {
    return <>
      {stepsRemaining} {stepsRemaining !== 1 ? 'steps' : 'step'} left
      &middot;
      About {approximateMinutesRemaining} min {minutesPer}
    </>
  }
  return <>All steps complete &#x1F389;</>
}

const SectionHeader = ({section, primaryGoal, primaryGoalDate, primaryGoalCompletedDate, onPrimaryGoalChange, onPrimaryGoalDateChange, onPrimaryGoalCompletedDateChange}: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const { data } = useContext(ChecklistContext)
  const unchecked = section.steps.filter(x => !isItemChecked(data, section.section, x.key))
  const stepsRemaining = unchecked.length
  const approximateMinutesRemaining = sum(unchecked, x => x.approximateMinutes)
  const minutesPer = section.steps?.[0].minutesPer
  const percentComplete = section.steps.length === 0 ? 1 : (section.steps.length - unchecked.length) / section.steps.length
  const isPrimaryGoal = section.section === primaryGoal

  const sectionCompleted = stepsRemaining < 1

  useEffect(() => {
    if(isPrimaryGoal && primaryGoalCompletedDate === null && sectionCompleted){
      onPrimaryGoalCompletedDateChange(dayjs().toISOString())
    }
    if(isPrimaryGoal && primaryGoalCompletedDate !== null && !sectionCompleted){
      onPrimaryGoalCompletedDateChange(null)
    }
  }, [sectionCompleted])

  return <div>
    {section.canBePrimaryGoal &&<Row onClick={event => event.stopPropagation()} justify='space-between' align='middle'>
      <span>
        {isPrimaryGoal && 
          <span style={{fontWeight: 'bold'}}>
            Goal Date: <DatePicker disabled={!currentUser?.isSuperUser} placeholder="Goal Date" defaultValue={primaryGoalDate ? dayjs(primaryGoalDate) : null} onChange={(value) => onPrimaryGoalDateChange(value?.toISOString())}/>
          </span>}
      </span>
      {<Tooltip title={isPrimaryGoal ? 'Current Primary Goal' : currentUser?.isSuperUser ? 'Set as Primary Goal' : null}>
        <Rate
          character={<FontAwesomeIcon icon={faFlagCheckered} />}
          style={{fontSize: '20px', color: 'red'}} 
          count={1} 
          value={isPrimaryGoal ? 1 : 0} 
          onChange={() => onPrimaryGoalChange(section.section)}
          disabled={!currentUser?.isSuperUser}/>
      </Tooltip>}
    </Row>}
    <Row justify='space-between' align='middle'>
      <Space>
        <SectionIcon section={section.section} checked={sectionCompleted} />
        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{section.title}</div>
      </Space>
      <span style={remainingStyle}>{buildRemaining(stepsRemaining, approximateMinutesRemaining, minutesPer)}</span>
    </Row>
    <Progress style={{ display: 'block', marginBottom: 0, lineHeight: '10px' }} percent={percentComplete * 100} showInfo={false} strokeColor={green.primary} />
  </div>
}

export default SectionHeader